import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lightning, CheckCircle, CurrencyBtc, ArrowRight } from "@phosphor-icons/react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import axios from "axios";
import BrandLogo from "@/components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PayPalGuest = ({ orderId, amount, label, onSuccess }) => {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { axios.get(`${API}/payments/paypal/config`).then(r => setCfg(r.data)); }, []);
  if (!cfg) return <div className="font-mono-k text-[#A1A1AA] text-sm">Loading PayPal…</div>;
  if (!cfg.enabled) return <div className="font-mono-k text-red-400 text-sm">PayPal not configured.</div>;
  return (
    <PayPalScriptProvider options={{
      "client-id": cfg.client_id,
      currency: "USD",
      intent: "capture",
      "enable-funding": "card,venmo,paylater",
      "disable-funding": "",
      components: "buttons",
    }}>
      <div className="bg-white p-3" data-testid="paypal-guest-wrapper">
        <PayPalButtons
          style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay", height: 45 }}
          createOrder={(_, actions) =>
            actions.order.create({
              intent: "CAPTURE",
              purchase_units: [{ reference_id: orderId, description: label, amount: { currency_code: "USD", value: amount.toFixed(2) } }],
              application_context: { shipping_preference: "NO_SHIPPING", user_action: "PAY_NOW", brand_name: "Korporate" },
            })
          }
          onApprove={async (data, actions) => {
            try {
              await actions.order.capture();
              await axios.post(`${API}/public/orders/${orderId}/paypal/capture`, {
                paypal_order_id: data.orderID,
                payer_id: data.payerID || "",
              });
              onSuccess();
            } catch (err) {
              toast.error(err?.response?.data?.detail || err?.message || "Capture failed");
            }
          }}
          onError={(err) => { if (process.env.NODE_ENV !== "production") console.warn("PayPal error:", err); toast.error("PayPal error"); }}
          onCancel={() => toast.message("Cancelled")}
        />
      </div>
    </PayPalScriptProvider>
  );
};

const ShopCheckout = () => {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("review");
  const [payError, setPayError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = () => axios.get(`${API}/public/orders/${orderId}`).then(r => {
      if (cancelled) return;
      setOrder(r.data);
      if (r.data.status === "paid") setStatus("done");
    }).catch(() => { toast.error("Order not found"); nav("/"); });
    load();

    const params = new URLSearchParams(window.location.search);
    if (params.get("paid") === "1") {
      setStatus("processing");
      const poll = setInterval(load, 3000);
      const timeout = setTimeout(() => { clearInterval(poll); setStatus("review"); toast.message("Still waiting on crypto confirmation."); }, 5 * 60_000);
      return () => { cancelled = true; clearInterval(poll); clearTimeout(timeout); };
    }
    return () => { cancelled = true; };
  }, [orderId, nav]);

  const startCrypto = async () => {
    setStatus("processing");
    setPayError("");
    try {
      const { data } = await axios.post(`${API}/public/orders/${orderId}/crypto/create-invoice`, { pay_currency: "usdttrc20" });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      throw new Error("No checkout URL");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed";
      setPayError(msg);
      toast.error(msg);
      setStatus("review");
    }
  };

  const onSuccess = () => { setStatus("done"); toast.success("Purchase confirmed! Check your email for delivery."); };

  if (!order) return <div className="min-h-screen bg-[#0A0D14]" />;
  const isPayPal = order.provider === "paypal";
  const isCrypto = order.provider === "nowpayments";

  return (
    <div className="min-h-screen bg-[#0A0D14] text-white flex items-center justify-center noise p-6">
      <div className="w-full max-w-lg">
        <a href={order.seller_slug ? `/u/${order.seller_slug}` : "/"} className="flex items-center gap-2 mb-8" data-testid="shop-checkout-back">
          <BrandLogo height={56} />
        </a>

        <div className="border border-white/10 bg-[#111318] p-8 brutal-shadow">
          <div className="text-xs font-mono-k tracking-widest-k text-[#FACC15] mb-3">
            — {isPayPal ? "PAYPAL / CARD CHECKOUT" : "CRYPTO CHECKOUT · USDT"}
          </div>
          <div className="font-display text-3xl tracking-tighter mb-2">{order.label}</div>

          {/* Line-item breakdown for cart orders */}
          {order.kind === "cart" && order.items?.length > 0 && (
            <div className="border-t border-b border-white/10 py-3 my-3 space-y-1" data-testid="shop-checkout-items">
              {order.items.map((it, i) => (
                <div key={i} className="flex justify-between text-xs font-mono-k text-[#DDDDDD]">
                  <span>{it.name} × {it.qty}</span>
                  <span>${Number(it.line_total).toFixed(2)}</span>
                </div>
              ))}
              {order.discount_pct > 0 && (
                <>
                  <div className="flex justify-between text-xs font-mono-k text-[#A1A1AA] pt-2 border-t border-white/10 mt-2">
                    <span>Subtotal</span><span>${Number(order.subtotal).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xs font-mono-k text-[#FACC15]">
                    <span>Bundle discount ({order.discount_pct}%)</span><span>-${Number(order.discount_amount).toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          )}

          <div className="font-mono-k text-4xl text-[#FACC15] mb-1" data-testid="shop-checkout-amount">${order.amount.toFixed(2)}</div>
          <div className="text-xs font-mono-k text-[#A1A1AA] mb-6">Receipt & delivery will be emailed to <span className="text-white">{order.buyer_email}</span></div>

          {status === "review" && isPayPal && (
            <div>
              <p className="text-sm font-mono-k text-[#A1A1AA] mb-4">Pay by PayPal balance OR any credit / debit card — no PayPal account required.</p>
              <PayPalGuest orderId={orderId} amount={order.amount} label={order.label} onSuccess={onSuccess} />
              <button onClick={() => nav(-1)} className="k-btn w-full justify-center mt-4" data-testid="shop-checkout-cancel">CANCEL</button>
            </div>
          )}

          {status === "review" && isCrypto && (
            <div>
              <p className="text-sm font-mono-k text-[#A1A1AA] mb-6">
                Pay ${order.amount.toFixed(2)} with USDT (TRC20 by default) via NOWPayments hosted checkout. Delivery unlocks automatically once the on-chain transaction confirms.
              </p>
              <button onClick={startCrypto} className="k-btn primary w-full justify-center mb-3" data-testid="shop-checkout-pay-crypto">
                <CurrencyBtc size={18} /> CONTINUE TO USDT PAYMENT <ArrowRight size={16} />
              </button>
              {payError && (
                <div className="border border-red-500/40 bg-red-500/10 text-red-300 text-xs font-mono-k p-3 mb-3" data-testid="shop-checkout-error">
                  {payError}
                </div>
              )}
              <button onClick={() => nav(-1)} className="k-btn w-full justify-center" data-testid="shop-checkout-cancel">CANCEL</button>
            </div>
          )}

          {status === "processing" && (
            <div className="text-center py-4"><div className="font-mono-k text-[#A1A1AA]">Waiting for payment confirmation…</div></div>
          )}

          {status === "done" && (
            <div className="text-center py-4" data-testid="shop-checkout-done">
              <CheckCircle size={64} weight="duotone" className="text-[#FACC15] mx-auto mb-4" />
              <div className="font-display text-2xl mb-2">Payment complete.</div>
              <div className="text-sm font-mono-k text-[#A1A1AA] mb-6">Your receipt and delivery details are on their way to {order.buyer_email}.</div>
              <a href={order.seller_slug ? `/u/${order.seller_slug}` : "/"} className="k-btn primary w-full justify-center" data-testid="shop-checkout-back-bio">BACK TO BIO <ArrowRight size={16} /></a>
            </div>
          )}
        </div>

        <div className="mt-4 text-center text-[10px] font-mono-k tracking-widest-k text-[#A1A1AA]">
          {isPayPal ? "PAYPAL SECURE CHECKOUT · Cards accepted" : "NOWPAYMENTS · USDT / BTC / ETH"}
        </div>
      </div>
    </div>
  );
};

export default ShopCheckout;
